import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTransition from '@/components/ui/PageTransition';
import { Button, Typography, Tag, App, Flex, theme } from 'antd';
import { EditOutlined, DeleteOutlined, TeamOutlined, FileTextOutlined, ThunderboltOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import { useQueryClient } from '@tanstack/react-query';
import {
  useDeleteCompanyMutation,
  useIssueTenantRoleTokenMutation,
  useUpdateAnyCompanyMemberRoleMutation,
  useRemoveAnyCompanyMemberMutation,
  useRemoveClientMemberMutation
} from '@/api/auth/mutations';
import { clientByIdQueryOptions, useClientByIdQuery, useClientCustomersQuery, useClientEmployeesQuery } from '@/api/auth/queries';
import type { UserWithClientMembership } from '@/api/auth/interfaces';
import { CompanyModal } from '@/components/tenant/CompanyModal';
import { TenantDetailState } from '@/components/tenant/TenantDetailState';
import { TenantInformationCard } from '@/components/tenant/TenantInformationCard';
import { TenantInviteModal } from '@/components/auth/TenantInviteModal';
import { AddClientMemberModal, AddCompanyMemberModal, TenantCompaniesTable } from '@/components/tenant';
import { EmployeesTable } from '@/components/employees';
import { TenantCustomersCard } from '@/components/customers';
import { TENANT_ROLES, type TenantRoleType } from '@/constants/tenantRoles';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyManagement } from '@/hooks/useCompanyManagement';
import { isAdminEquivalentRole } from '@/utils/authz';
import { DEFAULT_CLIENTS_LIST_ROUTE_SEARCH } from '@/api/auth/defaults';
import { authKeys } from '@/api/auth/keys';

const { Text } = Typography;

export const Route = createFileRoute('/admin/tenants/$tenantId/')({
  component: ClientDetailPage,
  loader: async ({ context, params }) => {
    // Prefetch the client data - this will block route rendering until data is loaded
    await context.queryClient.prefetchQuery(clientByIdQueryOptions(params.tenantId));
  }
});

function ClientDetailPage() {
  const { message } = App.useApp();
  const { t } = useTranslation('admin');
  const { tenantId } = Route.useParams();
  const { payload } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>();
  const deleteCompanyMutation = useDeleteCompanyMutation(tenantId);
  const companyManagement = useCompanyManagement(tenantId, {
    created: t('tenants.detail.messages.companyCreated'),
    updated: t('tenants.detail.messages.companyUpdated'),
    createError: (error) => t('tenants.detail.messages.companyCreateError', { error: error?.message || 'Unknown error' }),
    updateError: (error) => t('tenants.detail.messages.companyUpdateError', { error: error?.message || 'Unknown error' })
  });
  const issueTokenMutation = useIssueTenantRoleTokenMutation();

  // Add client member (admin only - uses GET /users)
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  // Add to company: selected user to add to a company
  const [addToCompanyUser, setAddToCompanyUser] = useState<UserWithClientMembership | null>(null);
  const updateCompanyRoleMutation = useUpdateAnyCompanyMemberRoleMutation(tenantId);
  const removeCompanyMemberMutation = useRemoveAnyCompanyMemberMutation(tenantId);
  const removeClientMemberMutation = useRemoveClientMemberMutation(tenantId);
  const { token } = theme.useToken();
  const canManageTenantMembers = isAdminEquivalentRole(payload?.role);

  const { data: clientData, error } = useClientByIdQuery(tenantId);

  // Fetch employees (owners + employees) - same as manage employees view
  const { data: employeesData, isLoading: employeesLoading } = useClientEmployeesQuery(tenantId);
  // Customers are listed by TenantCustomersCard; their IDs keep "Add member" from re-roling them by accident
  const { data: customersData } = useClientCustomersQuery(tenantId);

  /** Placeholder until tenant deletion is supported. */
  const deleteClient = () => {
    message.warning(t('tenants.detail.messages.deleteNotImplemented'));
  };

  /** Placeholder until tenant editing is supported. */
  const editClient = () => {
    message.warning(t('tenants.detail.messages.editNotImplemented'));
  };

  /** Issues an invite token for the tenant, scoped to the company when the invite context is a company. */
  const handleGenerateToken = async (params: {
    tenantRole: TenantRoleType;
    companyId?: string;
  }) => {
    const payload: { clientId: string; tenantRole: string; companyId?: string } = {
      clientId: tenantId,
      tenantRole: params.tenantRole
    };
    if (companyManagement.inviteCompanyId) {
      payload.companyId = companyManagement.inviteCompanyId;
    }
    const result = await issueTokenMutation.mutateAsync(payload);
    message.success(t('tenants.detail.token.tokenSuccess'));
    return result;
  };

  if (error) {
    return <TenantDetailState message={t('tenants.detail.errorLoading', { error: error.message })} />;
  }

  if (!clientData?.client) {
    return <TenantDetailState message={t('tenants.detail.clientNotFound')} />;
  }

  const client = clientData.client;
  const { inviteContext } = companyManagement;

  const tokenContextTitle =
    inviteContext.type !== 'company'
      ? t('tenants.detail.token.generatingForClient', { name: client.name })
      : t('tenants.detail.token.generatingForCompany', {
          name: client.companies.find((c) => c.id === inviteContext.companyId)?.name ?? ''
        });

  const users = employeesData?.users ?? [];
  const total = employeesData?.total ?? 0;
  const customerIds = (customersData?.users ?? []).map((customer) => customer.id);

  /** Resets the employees table search and role filter. */
  const handleClearFilters = () => {
    setSearch('');
    setRoleFilter(undefined);
  };

  return (
    <PageTransition animation="fadeInUp">
      <DetailPageHeader
        icon={<TeamOutlined />}
        title={client.name}
        meta={
          <>
            <Text code>{client.key}</Text>
            <Tag color="success" icon={<CheckCircleOutlined />} style={{ marginInlineEnd: 0 }}>
              {t('tenants.detail.active')}
            </Tag>
          </>
        }
        actions={
          <>
            <Link to={`/admin/tenants/${tenantId}/logs`}>
              <Button icon={<FileTextOutlined />}>{t('tenants.detail.viewLogs')}</Button>
            </Link>
            <Link to={`/admin/tenants/${tenantId}/event-triggers`}>
              <Button icon={<ThunderboltOutlined />}>{t('tenants.detail.eventTriggers.title')}</Button>
            </Link>
            <Button icon={<EditOutlined />} onClick={editClient}>
              {t('tenants.detail.editClient')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={deleteClient}>
              {t('tenants.detail.deleteClient')}
            </Button>
          </>
        }
        backTo="/admin/tenants"
        backSearch={DEFAULT_CLIENTS_LIST_ROUTE_SEARCH}
        backLabel={t('tenants.detail.backToClients')}
      />

      <Flex vertical gap={token.marginLG}>
        <TenantInformationCard client={client} onGenerateRegistrationLink={() => companyManagement.openInviteModal('client')} />

        <TenantCompaniesTable
          companies={client.companies}
          onAddCompany={companyManagement.openAddCompany}
          onEditCompany={companyManagement.openEditCompany}
          onGenerateInviteLink={(company) => companyManagement.openInviteModal('company', company.id)}
          onDeleteCompany={(company) => {
            deleteCompanyMutation.mutate(company.id, {
              onSuccess: () => message.success(t('tenants.detail.deleteCompany.success')),
              onError: (err: Error) => message.error(err?.message ?? t('tenants.detail.deleteCompany.error'))
            });
          }}
        />

        <EmployeesTable
          users={users}
          total={total}
          search={search}
          roleFilter={roleFilter}
          onSearchChange={setSearch}
          onRoleFilterChange={setRoleFilter}
          onClearFilters={handleClearFilters}
          isLoading={employeesLoading}
          clientId={tenantId}
          currentUserId={payload?.userId}
          canAddMemberFromList={canManageTenantMembers}
          onAddMemberClick={() => setAddMemberModalOpen(true)}
          clientCompanies={client.companies}
          onAddToCompanyClick={(user) => setAddToCompanyUser(user)}
          onChangeCompanyRole={(userId, companyId, role) =>
            updateCompanyRoleMutation.mutate({ companyId, userId, tenantRole: role })
          }
          onRemoveFromCompany={(userId, companyId) =>
            removeCompanyMemberMutation.mutate({ companyId, userId })
          }
          canRemoveFromTenant={canManageTenantMembers}
          onRemoveFromTenant={(userId) => removeClientMemberMutation.mutate(userId)}
        />

        <TenantCustomersCard
          clientId={tenantId}
          clientName={client.name}
          canManage={canManageTenantMembers}
          staffMemberIds={users.map((u) => u.id)}
          onInviteClick={() => companyManagement.openInviteModal('customer')}
        />
      </Flex>

      <CompanyModal
        open={companyManagement.companyModalOpen}
        loading={companyManagement.companySaving}
        company={companyManagement.editingCompany}
        onCancel={companyManagement.closeCompanyModal}
        onSubmit={companyManagement.saveCompany}
      />

      <TenantInviteModal
        open={companyManagement.inviteModalOpen}
        onCancel={companyManagement.closeInviteModal}
        onGenerate={handleGenerateToken}
        contextTitle={tokenContextTitle}
        companyId={companyManagement.inviteCompanyId}
        allowedRoles={inviteContext.type === 'customer' ? [TENANT_ROLES.CUSTOMER] : undefined}
        showAdminDetails
      />

      <AddClientMemberModal
        open={addMemberModalOpen}
        onCancel={() => setAddMemberModalOpen(false)}
        clientId={tenantId}
        clientName={client.name}
        existingMemberIds={[...users.map((u) => u.id), ...customerIds]}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: authKeys.client(tenantId) })}
      />

      {addToCompanyUser && (
        <AddCompanyMemberModal
          open={!!addToCompanyUser}
          onCancel={() => setAddToCompanyUser(null)}
          clientId={tenantId}
          clientMembers={users}
          companies={client.companies}
          initialUserId={addToCompanyUser.id}
          onSuccess={() => setAddToCompanyUser(null)}
        />
      )}
    </PageTransition>
  );
}
